import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactSelect, {
  type SelectOptionActionMeta,
  type CSSObjectWithLabel,
  type OptionProps,
  type FormatOptionLabelContext,
  type InputActionMeta,
} from 'react-select';
import debounce from 'lodash/debounce';
import styled from 'styled-components';

import { Text } from '@companycam/slab-web';
import { translate } from 'javascripts/translations/i18n';
import useProjectFeed from 'components/projects/graphql/useProjectFeed.js';
import { REACT_SELECT_STYLES } from 'helpers/styles';
import { type Project } from './types';

// #region styled-components
const StyledReactSelect = styled(ReactSelect)`
  input {
    opacity: 1 !important;
  }
`;

const ProjectName = styled(Text)<{ hasAddress?: boolean }>`
  font-size: var(--cc_size_text_m);
  margin: ${(props) => (props.hasAddress ? '0' : 'var(--cc_size_spacing_s) 0')};
`;

const ProjectAddress = styled(Text)`
  font-size: var(--cc_size_text_s);
  color: var(--cc_color_text_nonessential);
`;
// #endregion styled-components

type ProjectSelectProps = {
  onProjectSelect: (project: Project) => void;
  selectedProject: Project;
  placeholderText: string;
  filterProjects?: (project: Project) => boolean;
};

const ProjectSelect = ({
  onProjectSelect,
  selectedProject,
  placeholderText,
  filterProjects,
}: ProjectSelectProps) => {
  const [query, setQuery] = useState('');
  const selectRef = useRef(null);

  const setQueryValue = useMemo(
    () =>
      debounce((value: string) => {
        setQuery(value);
      }, 500),
    [setQuery],
  );

  const {
    projects,
    hasNextPage,
    fetchNextPage,
    fetchingNextPage,
    error,
    loading,
  } = useProjectFeed({
    feedType: 'ACTIVE',
    filters: {},
    query,
  });

  useEffect(() => {
    if (selectRef.current && !loading) {
      selectRef.current.focus();
    }
  }, [loading]);

  const handleInputChange = (
    inputValue: string,
    { action }: InputActionMeta,
  ) => {
    if (action === 'input-change') {
      setQueryValue(inputValue);

      const isProjectMismatch =
        selectedProject && inputValue !== selectedProject.node.formattedLine1;
      if (isProjectMismatch) {
        onProjectSelect(null);
      }
    }
    return inputValue;
  };

  const handleChange = (
    selectedOption: Project | null,
    { action }: SelectOptionActionMeta<Project>,
  ) => {
    onProjectSelect(selectedOption);

    if (action === 'select-option') {
      selectRef.current?.blur();
    }

    if (!selectedOption) {
      setQuery('');
    }
  };

  const handleScrollToBottom = () => {
    if (hasNextPage && !fetchingNextPage) {
      fetchNextPage();
    }
  };

  const displayNoProjectsMessage = () => {
    return error
      ? translate('project.reviews.reviewsHub.errorLoadingProjects')
      : translate('project.reviews.reviewsHub.noProjectsFound');
  };

  const formatOptionLabel = (
    project: Project,
    { context }: { context: FormatOptionLabelContext },
  ) => {
    if (context === 'menu') {
      return (
        <div>
          <ProjectName as="p" hasAddress={!!project.node.formattedLine2}>
            {project.node.formattedLine1}
          </ProjectName>
          {project.node.formattedLine2 ? (
            <ProjectAddress as="p">
              {project.node.formattedLine2}
            </ProjectAddress>
          ) : null}
        </div>
      );
    }

    return project.node.formattedLine1;
  };

  const customStyles = {
    ...REACT_SELECT_STYLES,
    menuPortal: (base: CSSObjectWithLabel) => ({
      ...base,
      zIndex: 1000,
    }),
    option: (base: CSSObjectWithLabel, state: OptionProps<Project>) => ({
      ...base,
      padding: 'var(--cc_size_spacing_s)',
      backgroundColor: state.isFocused
        ? 'var(--cc_color_background_3)'
        : 'var(--cc_color_background_1)',
      color: 'var(--cc_color_text_default)',
      '&:active': {
        backgroundColor: 'var(--cc_color_text_default)',
      },
    }),
  };

  const filteredProjects = filterProjects
    ? projects.filter((p: Project) => filterProjects(p))
    : projects;

  return (
    <StyledReactSelect
      ref={selectRef}
      options={filteredProjects}
      onChange={handleChange}
      onInputChange={handleInputChange}
      value={selectedProject}
      inputValue={
        selectedProject ? selectedProject.node.formattedLine1 : undefined
      }
      placeholder={placeholderText}
      noOptionsMessage={displayNoProjectsMessage}
      isSearchable
      isClearable
      filterOption={() => true}
      menuPortalTarget={document.body}
      onMenuScrollToBottom={handleScrollToBottom}
      styles={customStyles}
      formatOptionLabel={formatOptionLabel}
    />
  );
};

export default ProjectSelect;
